import {
  IClinicDtCapacityCell,
  IOpsClinic,
  IOpsClinicRow
} from 'model/ops';

/**
 * AXON-3585: shaping the clinic-wide DT capacity table.
 *
 * Pure functions over the query result, so the ordering and filtering rules can
 * be tested without rendering. Nothing here aggregates across clinics.
 */

/**
 * Column order agreed with the DT leads. Mirrors the backend constant
 * (constants/Axon/dtDisciplines.ts) and must stay in step with it: PT is not a
 * discipline this view reports on, so it draws no column here and its
 * clinicians are not queried there.
 */
export const DT_DISCIPLINE_ORDER = ['OT', 'SLP', 'MT'];

const byName = (a: IOpsClinic, b: IOpsClinic) =>
  (a.name || '').localeCompare(b.name || '');

/**
 * Hubs alphabetically, each followed by its spokes alphabetically.
 *
 * Spokes group under their top-most hub, so a chain deeper than one level still
 * renders, and a dangling or circular reference leaves a clinic at top level
 * rather than dropping it. Every active clinic passed in comes back exactly
 * once — the table must never quietly lose a site.
 */
export const buildClinicRows = (
  clinics: IOpsClinic[],
  capacity: IClinicDtCapacityCell[]
): IOpsClinicRow[] => {
  const active = (clinics || []).filter(clinic => clinic.isActive !== false);
  const ids = new Set(active.map(clinic => clinic.id));

  const cellsByClinic = new Map<number, IOpsClinicRow['cells']>();
  (capacity || []).forEach(cell => {
    const existing = cellsByClinic.get(cell.clinicId) || {};
    existing[cell.discipline] = cell;
    cellsByClinic.set(cell.clinicId, existing);
  });

  const byId = new Map(active.map(clinic => [clinic.id, clinic]));

  /**
   * The top-most hub a clinic belongs under. Walks the whole chain so a clinic
   * hanging off a spoke still lands somewhere visible, and gives up on a cycle
   * or a dangling reference by treating the clinic as its own root. Nothing can
   * fall out of the table.
   */
  const rootHubIdOf = (clinic: IOpsClinic): number => {
    const seen = new Set<number>([clinic.id]);
    let current = clinic;
    for (;;) {
      const hubId = current.clinicHub?.id;
      // No hub, or one outside the active list: `current` is the top of the chain.
      if (!hubId || !ids.has(hubId)) return current.id;
      // Revisiting a clinic means the chain loops. No member of a cycle can be
      // anyone's root, so each stands on its own rather than all of them
      // nesting into each other and leaving the table empty.
      if (seen.has(hubId)) return clinic.id;
      seen.add(hubId);
      current = byId.get(hubId)!;
    }
  };

  const spokesByHub = new Map<number, IOpsClinic[]>();
  const topLevel: IOpsClinic[] = [];
  active.forEach(clinic => {
    const rootId = rootHubIdOf(clinic);
    if (rootId === clinic.id) {
      topLevel.push(clinic);
      return;
    }
    const siblings = spokesByHub.get(rootId) || [];
    siblings.push(clinic);
    spokesByHub.set(rootId, siblings);
  });

  const toRow = (clinic: IOpsClinic, isSpoke: boolean): IOpsClinicRow => ({
    id: clinic.id,
    name: clinic.name || '',
    isSpoke,
    cells: cellsByClinic.get(clinic.id) || {}
  });

  const rows: IOpsClinicRow[] = [];
  topLevel.sort(byName).forEach(hub => {
    rows.push(toRow(hub, false));
    (spokesByHub.get(hub.id) || [])
      .sort(byName)
      .forEach(spoke => rows.push(toRow(spoke, true)));
  });
  return rows;
};

/**
 * Narrow by clinic name, keeping whole hub groups.
 *
 * A match on a hub keeps its spokes, and a match on a spoke keeps the hub above
 * it — so an indented row can never render without its parent.
 */
export const filterClinicRows = (
  rows: IOpsClinicRow[],
  term: string
): IOpsClinicRow[] => {
  const needle = (term || '').trim().toLowerCase();
  if (!needle) return rows;

  const matches = (row: IOpsClinicRow) =>
    row.name.toLowerCase().includes(needle);

  const kept: IOpsClinicRow[] = [];
  let index = 0;
  while (index < rows.length) {
    // Walk forward over the hub and its spokes; the group is kept or dropped
    // whole, so a spoke can never render without its hub.
    let next = index + 1;
    while (next < rows.length && rows[next].isSpoke) next += 1;

    const group = rows.slice(index, next);
    if (group.some(matches)) kept.push(...group);
    index = next;
  }
  return kept;
};

/**
 * Disciplines worth a column. A discipline nobody staffs draws no column at all
 * — never a column of zeros.
 *
 * Pass every clinic loaded rather than the filtered subset: the column set is a
 * property of the estate, not of what is typed in the search box, so narrowing
 * to one quiet clinic must not take the headers away with it.
 */
export const visibleDisciplines = (rows: IOpsClinicRow[]): string[] =>
  DT_DISCIPLINE_ORDER.filter(discipline =>
    (rows || []).some(row => (row.cells[discipline]?.clinicians || 0) > 0)
  );
